/**
 * PostDetailOverlay — 게시글 상세 오버레이
 *
 * 게시글 목록 위에 슬라이드인하여 표시됨.
 * 게시글 내용 + 좋아요 + 수정/삭제 + 댓글 섹션.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { useBoardContext } from '../../context/BoardContext';
import { Comment as CommentModel, PostAttachment, Post } from '../../models/Board';
import {
  fetchPost, fetchComments, toggleReaction, deletePost, updatePost,
  updateIssueStatus, updateIssueAssignee, updateIssueDueDate, updateIssuePriority,
  uploadBoardImage, uploadPostAttachment, markPostCommentRead, recordPostRead, getPostReadReceipts,
  fetchPageTree, getUsedTags,
} from '../../services/BoardService';
import TagInput from './TagInput';
import {
  getIssueMetadata, IssueStatus, IssuePriority,
  ISSUE_STATUS_CONFIG, ISSUE_PRIORITY_CONFIG, getIssueStatusLabel, getIssuePriorityLabel,
  ISSUE_STATUS_LIST, ISSUE_PRIORITY_LIST,
  getDaysUntilDue, canWriteComment, canManageBoard, canUseAllMention,
} from '../../models/Board';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { MyNotifs } from '../../services/MyNotifs';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { useHistory } from '../../hooks/useRouterCompat';
import { useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { IconSmilePlus } from '../components/FigmaIcons';
import {
  ArrowLeft, Eye, MessageCircle,
  Pencil, Trash2, X, Check, AlertTriangle,
  Pin, AlertCircle, Info, CheckCircle2, Circle,
  Calendar, User, ChevronDown, ListTree, MoreHorizontal, FolderTree,
  Link2, ShieldCheck, Paperclip, Loader2,
  HelpCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from '../../components/ui/use-toast';
import CommentSection from './CommentSection';
import BoardRichEditor from './BoardRichEditor';
import { extractTocFromJson, TocItem } from './tocUtils';
import TocHelpModal from './TocHelpModal';
import PostCreateModal from './PostCreateModal';
import RevisionHistoryDialog from './RevisionHistoryDialog';
import { UserAvatar } from '../components/UserAvatar';
import MessageFileItem from '../components/MessageFileItem';
import { FileAttachment } from '../../models/Message';
import { useAppUIContext } from '../../context/AppUIContext';
import { extractMentionedUserIds, MentionUser } from './MentionSuggestion';

const PostDetailOverlay: React.FC = () => {
  const { selectedPost, setSelectedPost, refreshPosts, selectedBoard } = useBoardContext();
  const { setMessageToShow } = useAppUIContext();
  const [post, setPost] = useState(selectedPost);
  const [comments, setComments] = useState<CommentModel[]>([]);
  const [isLiking, setIsLiking] = useState(false);
  const [showReactionPopover, setShowReactionPopover] = useState(false);

  // 수정 모달 — PostCreateModal 을 editingPost prop 으로 재사용
  const [showEditModal, setShowEditModal] = useState(false);

  // 이전 인라인 편집 모드 잔여물 — 모달 통합으로 항상 false. 아래 isEditing 분기 UI 는 dead code (다음 정리 사이클에서 제거).
  const isEditing = false as const;

  /** 모바일/태블릿(xl 미만)에서 본문 목차 드로어 열림 여부 */
  const [tocOpen, setTocOpen] = useState(false);
  /** 목차 도움말 모달 */
  const [tocHelpOpen, setTocHelpOpen] = useState(false);

  // 삭제 확인
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // 중요도 팝오버
  const [showImportancePicker, setShowImportancePicker] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 수정 이력
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);

  // @멘션 멤버
  const [mentionMembers, setMentionMembers] = useState<MentionUser[]>([]);

  // 위키 페이지 목록 (위키링크 자동완성 + 네비게이션용)
  const [wikiPagesForMention, setWikiPagesForMention] = useState<{ kind: 'page'; id: string; title: string }[]>([]);
  // 위키 게시판 전체 페이지 (breadcrumb 부모 체인 계산용 — 현재 페이지 포함)
  const [allWikiPages, setAllWikiPages] = useState<{ id: string; title: string; parent_post_id: string | null }[]>([]);

  // 열람 추적 (Read Receipts)
  const [readReceipts, setReadReceipts] = useState<{ user_id: string; read_at: string }[]>([]);
  const [showReadReceipts, setShowReadReceipts] = useState(false);
  const [issuePropsExpanded, setIssuePropsExpanded] = useState(false);

  // 멤버 로드 (채널 또는 워크스페이스)
  useEffect(() => {
    if (selectedBoard?.channel_id) {
      const room = My.Rooms.get(selectedBoard.channel_id);
      if (room) {
        const others = room.users.filter(u => u.id !== My.user?.id);
        setMentionMembers(
          others.map(u => ({
            id: u.id,
            name: u.name,
            profileUrl: u.photoUrl ?? null,
          }))
        );
      }
    } else if (selectedBoard?.workspace_id) {
      Fire.ws.getWorkspaceMembers(selectedBoard.workspace_id).then((members) => {
        setMentionMembers(
          members
            .filter(m => m.id !== My.user?.id)
            .map(m => ({
              id: m.id,
              name: m.user.name,
              profileUrl: m.user.photoUrl ?? null,
            }))
        );
      }).catch(() => {});
    }
  }, [selectedBoard?.workspace_id, selectedBoard?.channel_id]);

  // 위키 게시판: 페이지 목록 로드 (위키링크 자동완성 + breadcrumb 부모 체인)
  useEffect(() => {
    if (selectedBoard?.type === 'wiki') {
      fetchPageTree(selectedBoard.id)
        .then(pages => {
          setAllWikiPages(pages.map(p => ({
            id: p.id, title: p.title, parent_post_id: p.parent_post_id,
          })));
          setWikiPagesForMention(
            pages
              .filter(p => p.id !== selectedPost?.id) // 자기 자신 링크 방지
              .map(p => ({ kind: 'page' as const, id: p.id, title: p.title })),
          );
        })
        .catch(err => console.warn('[PostDetailOverlay] 위키 페이지 로드 실패:', err));
    } else {
      setWikiPagesForMention([]);
      setAllWikiPages([]);
    }
  }, [selectedBoard?.id, selectedBoard?.type, selectedPost?.id]);

  // breadcrumb: 현재 페이지의 부모 체인 (루트부터 부모까지)
  // (selectedBoard.type 직접 참조 — isWikiBoard 변수는 아래서 선언됨)
  const wikiBreadcrumb = useMemo(() => {
    if (selectedBoard?.type !== 'wiki' || !post?.id) return [] as { id: string; title: string }[];
    const byId = new Map(allWikiPages.map(p => [p.id, p]));
    const chain: { id: string; title: string }[] = [];
    let cur = byId.get(post.id)?.parent_post_id ?? null;
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      const node = byId.get(cur);
      if (!node) break;
      chain.unshift({ id: node.id, title: node.title });
      cur = node.parent_post_id;
    }
    return chain;
  }, [selectedBoard?.type, post?.id, allWikiPages]);

  // 게시글 상세 + 댓글 로드
  useEffect(() => {
    if (!selectedPost) return;
    setPost(selectedPost);
    setShowEditModal(false);
    setShowDeleteConfirm(false);
    setShowReadReceipts(false);
    setReadReceipts([]);

    fetchPost(selectedPost.id).then(p => {
      if (p) {
        setPost(p);
        // 멘션된 게시글이면 열람 기록 + 확인 현황 로드
        const hasMentions = p.mentioned_user_ids && p.mentioned_user_ids.length > 0;
        if (hasMentions && My.user?.id) {
          // 본인이 멘션된 경우 열람 기록
          if (p.mentioned_user_ids!.includes(My.user.id)) {
            recordPostRead(p.id, My.user.id);
          }
          // 확인 현황 데이터 로드 (작성자 또는 관리자용)
          getPostReadReceipts(p.id).then(setReadReceipts);
        }
      }
    });
    fetchComments(selectedPost.id).then(setComments);
    // 댓글 미읽음 시점 기록
    markPostCommentRead(selectedPost.id, My.user?.id!);
    // 게시글 열람 → 해당 게시글의 미읽음 알림 읽음 처리 (알림 클릭이든 게시판에서 직접 열람이든).
    // 게시판 알림은 클릭이 아니라 게시글을 실제 열었을 때 읽음 처리하는 게 맞음.
    const unreadNotifs = MyNotifs.Notifications.filter(
      n => n.isNew && n.source === 'board' && n.postId === selectedPost.id,
    );
    if (unreadNotifs.length) Fire.clearNotifications(unreadNotifs);
  }, [selectedPost?.id]);

  const isMyPost = post && My.user?.id === post.author.uid;
  const myRole = useMyRole();
  const history = useHistory();

  /**
   * 위키 페이지의 원본 채팅 메시지를 모달로 표시.
   * 알림 모달/검색 결과처럼 setMessageToShow 로 MessageModal 을 띄워
   * 현재 화면(페이지 상세) 이동 없이 미리보기.
   * 메시지 ID 가 없으면 채널로 이동 (출처 정보만 있는 경우 fallback).
   */
  const handleSourceChatClick = useCallback(async () => {
    const roomId = post?.metadata?.source_room_id;
    if (!roomId) return;
    const messageId: string | null = post?.metadata?.source_thread_id ?? null;
    if (!messageId) {
      history.push(`/channel/${roomId}`);
      return;
    }
    try {
      const msg = await Fire.msg.getMessage(messageId);
      if (msg) {
        setMessageToShow(msg);
      } else {
        // 메시지가 삭제됐거나 못 찾으면 채널로 이동
        history.push(`/channel/${roomId}`);
      }
    } catch (err) {
      console.warn('[PostDetailOverlay] 원본 메시지 로드 실패:', err);
      history.push(`/channel/${roomId}`);
    }
  }, [post?.metadata, history, setMessageToShow]);
  const isAdmin = myRole === 'owner' || myRole === 'admin';
  const wsSettings = useWorkspaceSettings();
  const canEditOrDelete = isMyPost
    || (selectedBoard ? canManageBoard(selectedBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids) : false);
  const canComment = selectedBoard ? canWriteComment(selectedBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids) : true;
  const canMentionAll = selectedBoard ? canUseAllMention(selectedBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids) : false;
  const isNoticeBoard = selectedBoard?.type === 'notice';
  const isQnaBoard = selectedBoard?.type === 'qna';
  const isIssueBoard = selectedBoard?.type === 'issue';
  const issueData = isIssueBoard && post ? getIssueMetadata(post) : null;
  const isWikiBoard = selectedBoard?.type === 'wiki';

  // 본문 영역 폭 기반 반응형 — 채널 분할 뷰처럼 컨테이너가 좁을 때 viewport 와 무관하게
  // 모바일 스타일(작은 폰트·TOC 숨김)을 적용한다. ResizeObserver 로 실제 폭 측정.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setIsNarrow(w < 880);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Wiki TOC — 저장된 post.content 기반 (편집 중에는 미리보기 모달에서 별도 표시)
  const tocItems = useMemo<TocItem[]>(() => {
    if (!isWikiBoard || !post?.content || isEditing) return [];
    return extractTocFromJson(post.content);
  }, [isWikiBoard, post?.content, isEditing]);

  // Issue 상태 변경 UI
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showPriorityPicker, setShowPriorityPicker] = useState(false);

  /** 채널 멤버 목록 (issue 게시판의 담당자 선택용) */
  const channelMembers = useMemo(() => {
    if (!isIssueBoard || !selectedBoard?.channel_id) return [];
    const room = My.Rooms.get(selectedBoard.channel_id);
    if (!room) return [];
    return room.users.map(u => ({ id: u.id, name: u.name, photoUrl: u.photoUrl }));
  }, [isIssueBoard, selectedBoard?.channel_id]);

  // 진입 출처가 대시보드면 '← 목록' 대신 '← 대시보드' 로 보여주고 /board 로 복귀.
  // location.state 는 history.push(path, state) 로 전달된 그 entry 의 state.
  const location = useLocation() as { state?: { fromDashboard?: boolean } };
  const fromDashboard = location.state?.fromDashboard === true;

  const handleClose = useCallback(() => {
    if (fromDashboard) {
      history.push('/board');
      return;
    }
    setSelectedPost(null);
    if (selectedBoard) {
      refreshPosts(selectedBoard.id);
    }
  }, [fromDashboard, history, setSelectedPost, refreshPosts, selectedBoard]);

  const handleReaction = useCallback(async (reactionType: string) => {
    if (!post || isLiking || !My.user?.id) return;
    setIsLiking(true);
    try {
      const updated = await toggleReaction(post.id, My.user.id, reactionType);
      setPost(updated);
    } catch (err) {
      console.error('[PostDetail] 리액션 실패:', err);
      toast({ title: t`Failed to add reaction`, variant: 'destructive' });
    } finally {
      setIsLiking(false);
    }
  }, [post, isLiking]);

  // ── 수정 ── 모달로 통합 (PostCreateModal 의 editingPost prop 사용)
  const handleEditStart = () => {
    if (!post) return;
    setShowEditModal(true);
  };

  /** 모달에서 저장 성공 시 selectedPost 갱신 — 새 데이터로 즉시 반영 */
  const handleEditSaved = (updated: Post) => {
    setPost(updated);
  };

  // ── 고정 토글 ──
  const handleTogglePin = async () => {
    if (!post) return;
    try {
      const updated = await updatePost(post.id, { is_pinned: !post.is_pinned });
      setPost(updated);
      toast({ title: updated.is_pinned ? t`Post pinned` : t`Post unpinned` });
    } catch (err) {
      toast({ title: t`Failed to change pin`, variant: 'destructive' });
    }
  };

  // ── 중요도 변경 ──
  const handleChangeImportance = async (value: string) => {
    if (!post) return;
    const newMeta = { ...(post.metadata ?? {}), importance: value || undefined };
    // importance가 빈 문자열이면 키 제거
    if (!value) delete newMeta.importance;
    try {
      const updated = await updatePost(post.id, { metadata: Object.keys(newMeta).length > 0 ? newMeta : null });
      setPost(updated);
      setShowImportancePicker(false);
      const label = value === 'critical' ? t`Important` : value === 'info' ? t`Info` : t`Normal`;
      toast({ title: t`Importance changed to '${label}'` });
    } catch (err) {
      toast({ title: t`Failed to change importance`, variant: 'destructive' });
    }
  };

  // ── 삭제 ──
  const handleDelete = async () => {
    if (!post || isDeleting) return;
    setIsDeleting(true);
    try {
      await deletePost(post.id);
      setSelectedPost(null);
      if (selectedBoard) {
        refreshPosts(selectedBoard.id);
      }
    } catch (err) {
      console.error('[PostDetail] 삭제 실패:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCommentAdded = useCallback(async () => {
    if (!post) return;
    const updated = await fetchPost(post.id);
    if (updated) setPost(updated);
    const cmts = await fetchComments(post.id);
    setComments(cmts);
  }, [post]);

  if (!post) return null;

  // 현재 유저의 리액션 타입 확인
  const myReaction = (() => {
    if (!My.user?.id) return null;
    for (const [type, uids] of Object.entries(post.reactions)) {
      if (uids.includes(My.user.id)) return type;
    }
    return null;
  })();

  return (
    <div className="flex flex-col h-full bg-background">
      {/* 패널 — 전체 영역 사용 */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* ── Header ── */}
        <header className="flex items-center gap-2 px-4 h-12 shrink-0 border-b border-border">
          <button
            onClick={handleClose}
            className="flex items-center gap-1.5 h-8 px-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
          >
            <ArrowLeft className="size-4" />
            <span>{fromDashboard ? <Trans>Dashboard</Trans> : <Trans>List</Trans>}</span>
          </button>

          {/* 위키 게시판: 부모 페이지 path (── 목록 옆에 인라인) */}
          {isWikiBoard && !isEditing && (
            <nav className="flex items-center gap-1 text-xs text-muted-foreground min-w-0 flex-1 overflow-hidden">
              <span className="opacity-50 shrink-0">|</span>
              {wikiBreadcrumb.length > 0 && wikiBreadcrumb.map((node) => (
                <React.Fragment key={node.id}>
                  <button
                    onClick={() => {
                      fetchPost(node.id).then(p => { if (p) setSelectedPost(p); });
                    }}
                    className="hover:text-foreground hover:underline truncate max-w-[140px] cursor-pointer shrink-0"
                  >
                    {node.title || t`Untitled`}
                  </button>
                  <span className="opacity-50 shrink-0">›</span>
                </React.Fragment>
              ))}
              <span className="text-foreground/80 font-medium truncate min-w-0">
                {post?.title || t`Untitled`}
              </span>
            </nav>
          )}

          {!isWikiBoard && <div className="flex-1" />}

          {/* notice 전용: 고정/중요도 */}
          {isNoticeBoard && !isEditing && (
            <div className="flex items-center gap-0.5 shrink-0 border-r border-border pr-1.5 mr-0.5">
              {/* 고정 토글 */}
              <button
                onClick={handleTogglePin}
                title={post.is_pinned ? t`Unpin` : t`Pin to top`}
                className={cn(
                  'flex items-center gap-1 h-7 px-2 rounded-md text-meta transition-colors cursor-pointer',
                  post.is_pinned
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                )}
              >
                <Pin className={cn('size-3', post.is_pinned && 'fill-primary/30')} strokeWidth={post.is_pinned ? 2.5 : 1.5} />
                <span className="hidden sm:inline">{post.is_pinned ? <Trans>Pinned</Trans> : <Trans>Pin</Trans>}</span>
              </button>
              {/* 중요도 */}
              <div className="relative">
                <button
                  onClick={() => setShowImportancePicker(!showImportancePicker)}
                  title={t`Importance`}
                  className={cn(
                    'flex items-center gap-1 h-7 px-2 rounded-md text-meta transition-colors cursor-pointer',
                    (post.metadata?.importance === 'critical') ? 'text-destructive bg-destructive/10'
                      : (post.metadata?.importance === 'info') ? 'text-warning bg-warning/10'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                >
                  {post.metadata?.importance === 'critical' ? <AlertCircle className="size-3" />
                    : post.metadata?.importance === 'info' ? <Info className="size-3" />
                      : <AlertCircle className="size-3 opacity-50" />}
                  <span className="hidden sm:inline">
                    {post.metadata?.importance === 'critical' ? <Trans>Important</Trans> : post.metadata?.importance === 'info' ? <Trans>Info</Trans> : <Trans>Normal</Trans>}
                  </span>
                </button>
                {showImportancePicker && (
                  <div className="absolute right-0 top-full mt-1 w-28 rounded-xl border border-border bg-background shadow-lg z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                    {[{ v: '', l: t`Normal` }, { v: 'info', l: t`🟡 Info` }, { v: 'critical', l: t`🔴 Important` }].map(o => (
                      <button
                        key={o.v}
                        onClick={() => handleChangeImportance(o.v)}
                        className={cn(
                          'w-full text-left px-3 py-1.5 text-label hover:bg-muted transition-colors cursor-pointer',
                          (post.metadata?.importance ?? '') === o.v && 'font-semibold text-primary',
                        )}
                      >{o.l}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* QnA 전용: 해결 상태 뱃지 */}
          {isQnaBoard && !isEditing && (
            <div className="flex items-center gap-1 shrink-0 border-r border-border pr-1.5 mr-0.5">
              {post.metadata?.is_resolved ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-meta-sm font-semibold bg-badge-success-bg text-badge-success">
                  <CheckCircle2 className="size-3.5" />
                  <Trans>Resolved</Trans>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-meta-sm font-semibold bg-badge-warn-bg text-badge-warn">
                  <Circle className="size-3.5" />
                  <Trans>Unresolved</Trans>
                </span>
              )}
            </div>
          )}

          {/* Issue 전용: 상태 + 우선순위 */}
          {isIssueBoard && !isEditing && issueData && (
            <div className="flex items-center gap-0.5 shrink-0 border-r border-border pr-1.5 mr-0.5">
              {/* 상태 */}
              <div className="relative">
                <button
                  onClick={() => { setShowStatusPicker(!showStatusPicker); setShowPriorityPicker(false); }}
                  className={cn(
                    'flex items-center gap-1 h-7 px-2 rounded-md text-meta transition-colors cursor-pointer',
                    ISSUE_STATUS_CONFIG[issueData.status].color,
                    ISSUE_STATUS_CONFIG[issueData.status].bgColor,
                  )}
                >
                  <span className="font-semibold">{getIssueStatusLabel(issueData.status)}</span>
                  <ChevronDown className="size-3" />
                </button>
                {showStatusPicker && (
                  <div className="absolute left-0 top-full mt-1 w-32 rounded-xl border border-border bg-background shadow-lg z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                    {ISSUE_STATUS_LIST.map(s => {
                      const cfg = ISSUE_STATUS_CONFIG[s];
                      return (
                        <button
                          key={s}
                          onClick={async () => {
                            setShowStatusPicker(false);
                            const updated = await updateIssueStatus(post.id, post.metadata, s);
                            setPost(updated);
                            toast({ title: t`Status changed to ${getIssueStatusLabel(s)}` });
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-1.5 text-meta transition-colors cursor-pointer',
                            issueData.status === s ? 'bg-muted font-semibold' : 'hover:bg-muted/60',
                          )}
                        >
                          <span className={cfg.color}>{getIssueStatusLabel(s)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 우선순위 */}
              <div className="relative">
                <button
                  onClick={() => { setShowPriorityPicker(!showPriorityPicker); setShowStatusPicker(false); }}
                  className={cn(
                    'flex items-center gap-1 h-7 px-2 rounded-md text-meta transition-colors cursor-pointer',
                    ISSUE_PRIORITY_CONFIG[issueData.priority].color,
                    'hover:bg-muted',
                  )}
                >
                  <span>{ISSUE_PRIORITY_CONFIG[issueData.priority].icon}</span>
                  <span className="hidden sm:inline font-medium">{getIssuePriorityLabel(issueData.priority)}</span>
                </button>
                {showPriorityPicker && (
                  <div className="absolute left-0 top-full mt-1 w-28 rounded-xl border border-border bg-background shadow-lg z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                    {ISSUE_PRIORITY_LIST.map(p => {
                      const cfg = ISSUE_PRIORITY_CONFIG[p];
                      return (
                        <button
                          key={p}
                          onClick={async () => {
                            setShowPriorityPicker(false);
                            const updated = await updateIssuePriority(post.id, post.metadata, p);
                            setPost(updated);
                            toast({ title: t`Priority changed to ${getIssuePriorityLabel(p)}` });
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-1.5 text-meta transition-colors cursor-pointer',
                            issueData.priority === p ? 'bg-muted font-semibold' : 'hover:bg-muted/60',
                          )}
                        >
                          <span className={cfg.color}>{cfg.icon} {getIssuePriorityLabel(p)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 본문 목차 토글 — 모바일/태블릿(xl 미만)에서만 노출, 헤딩이 1개 이상일 때 */}
          {!isEditing && tocItems.length > 0 && (
            <button
              onClick={() => setTocOpen(true)}
              className="lg:hidden ml-auto size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
              aria-label={t`Open table of contents`}
              title={t`Table of contents`}
            >
              <ListTree className="size-4" />
            </button>
          )}

          {/* 수정 모드 액션은 본문 박스 안 제목 옆으로 이동 — 보기 모드의 [⋯] 자리와 동일 위치 */}
        </header>

        {/* ── 삭제 확인 배너 ── */}
        {showDeleteConfirm && (
          <div className="flex items-center gap-3 px-4 py-3 bg-destructive/8 border-b border-destructive/20 animate-in fade-in duration-150">
            <AlertTriangle className="size-4 text-destructive shrink-0" />
            <div className="flex-1">
              <span className="text-sm text-destructive"><Trans>Delete this post? This cannot be undone.</Trans></span>
              {!isMyPost && isAdmin && (
                <p className="text-meta-sm text-badge-warn mt-0.5 inline-flex items-center gap-1">
                  <ShieldCheck className="size-3 shrink-0" />
                  <Trans>You are deleting another user's post with admin permission. This action is logged.</Trans>
                </p>
              )}
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? <Trans>Deleting…</Trans> : <Trans>Delete</Trans>}
              </Button>
            </div>
          </div>
        )}

        {/* ── Body ── */}
        <div ref={bodyRef} className={cn(
          'flex-1 overflow-y-auto flex',
          isWikiBoard ? 'bg-background' : 'bg-muted/20'
        )}>
          {/* 본문 영역 */}
          <div className="flex-1 overflow-y-auto min-w-0">
            <article className={cn(
              'max-w-[1100px] mx-auto pb-2',
              isWikiBoard ? 'px-4 pt-6 sm:pt-8' : 'px-4 md:px-6 pt-6'
            )}>
              <div className={cn(
                isWikiBoard ? '' : 'bg-card border border-border rounded-xl shadow-sm px-6 md:px-8 py-6 md:py-8'
              )}>
              {/* breadcrumb 는 페이지 헤더 좌측 (← 목록 옆) 인라인으로 이동됨 */}

              {/* 채팅에서 가져온 위키 페이지: 출처 배지 (원본 채팅방·메시지로 점프) */}
              {isWikiBoard && !isEditing && post.metadata?.source_room_id && (
                <button
                  type="button"
                  onClick={handleSourceChatClick}
                  className="inline-flex items-center gap-1.5 text-meta-sm text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted px-2 py-1 rounded-md mb-2 transition-colors"
                  title={post.metadata?.source_thread_id ? t`Go to original chat message` : t`Go to original channel`}
                >
                  <Link2 className="size-3" />
                  <span>
                    <Trans>From chat</Trans> ·{' '}
                    {new Date(post.created_at).toLocaleDateString(undefined, {
                      month: '2-digit', day: '2-digit',
                    })}
                  </span>
                  {post.metadata?.ai_generated && (
                    <span className="text-primary/70">· <Trans>AI summary</Trans></span>
                  )}
                </button>
              )}

              {/* 제목 + 본문 액션 영역.
                  보기 모드: [핀(wiki)] [⋯ 더보기 → 수정/삭제]
                  편집 모드: [취소] [저장]
                  → 같은 자리에서 모드별 액션 — 시선 이동 최소화. */}
              <div className="flex items-start gap-3">
                <h1 className={cn(
                  'font-bold text-foreground leading-[1.25] flex-1 min-w-0 tracking-tight',
                  isNarrow ? 'text-xl' : 'text-2xl md:text-3xl',
                )}>
                  {post.title}
                </h1>
                <div className="flex items-center gap-1 shrink-0">
                  {/* 보기 모드 — 핀 + 더보기 */}
                  {!isEditing && (
                    <>
                      {isWikiBoard && (
                        <Button
                          variant="icon-panel"
                          type="button"
                          data-state={post.is_pinned ? 'on' : undefined}
                          onClick={async () => {
                            if (!post) return;
                            try {
                              if (!post.is_pinned) {
                                const all = await fetchPageTree(post.board_id);
                                const currentPins = all.filter(p => p.is_pinned && p.id !== post.id).length;
                                if (currentPins >= 3) {
                                  toast({ title: t`You can pin up to 3 pages`, variant: 'destructive' });
                                  return;
                                }
                              }
                              const updated = await updatePost(post.id, { is_pinned: !post.is_pinned });
                              setPost(updated);
                              if (selectedBoard) refreshPosts(selectedBoard.id);
                              toast({ title: updated.is_pinned ? t`Page pinned` : t`Page unpinned` });
                            } catch (err) {
                              console.error('[PostDetailOverlay] 핀 토글 실패:', err);
                              toast({ title: t`Failed to toggle pin`, variant: 'destructive' });
                            }
                          }}
                          title={post.is_pinned ? t`Unpin` : t`Pin to sidebar (max 3)`}
                        >
                          <Pin />
                        </Button>
                      )}
                      {canEditOrDelete && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="icon-panel"
                              type="button"
                              title={t`More`}
                            >
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" sideOffset={4}>
                            {!isMyPost && isAdmin && (
                              <div className="px-2 py-1 text-meta-sm text-badge-warn font-medium flex items-center gap-1">
                                <ShieldCheck className="size-3" />
                                <Trans>Admin permission</Trans>
                              </div>
                            )}
                            <DropdownMenuItem onClick={handleEditStart}>
                              <Pencil className="mr-2 size-3.5" />
                              <Trans>Edit</Trans>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setShowDeleteConfirm(true)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 size-3.5" />
                              <Trans>Delete</Trans>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </>
                  )}
                  {/* 편집 모드 액션 (미리보기/취소/저장) 은 PostCreateModal editingPost 푸터로 통합됨 */}
                </div>
              </div>

              {/* 작성자 메타 — 아바타 + 이름/날짜 + 통계 */}
              <div className="flex items-center gap-3 mt-5">
                <UserAvatar
                  src={post.author.photoUrl}
                  name={post.author.name}
                  className="size-8"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-foreground leading-none">{post.author.name}</span>
                  <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                    <span>
                      {new Date(post.created_at).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                      {' '}
                      {new Date(post.created_at).toLocaleTimeString('ko-KR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    {post.updated_at && (
                      <button
                        type="button"
                        onClick={() => setShowRevisionHistory(true)}
                        className="text-muted-foreground/60 hover:text-primary transition-colors cursor-pointer underline-offset-2 hover:underline"
                      >
                        (<Trans>edited</Trans>)
                      </button>
                    )}
                  </div>
                </div>
                {/* 우측 통계 */}
                <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Eye className="size-3.5" />
                    <span className="tabular-nums">{post.view_count}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="size-3.5" />
                    <span className="tabular-nums">{post.comment_count}</span>
                  </span>
                </div>
              </div>
              <hr className="my-6 border-border" />

              {/* 확인 현황 (멘션된 게시글만 표시) — 기본 아바타 줄, 클릭 시 Popover */}
              {post.mentioned_user_ids && post.mentioned_user_ids.length > 0 && (() => {
                const mentionedIds = post.mentioned_user_ids!;
                const readSet = new Set(readReceipts.map(r => r.user_id));
                const readMap = Object.fromEntries(readReceipts.map(r => [r.user_id, r.read_at]));
                const confirmedCount = mentionedIds.filter(id => readSet.has(id)).length;
                const totalCount = mentionedIds.length;

                const getMemberName = (uid: string) => {
                  const member = mentionMembers.find(m => m.id === uid);
                  return member?.name ?? uid.slice(0, 8);
                };
                const getMemberPhoto = (uid: string) => {
                  const member = mentionMembers.find(m => m.id === uid);
                  return member?.profileUrl ?? null;
                };

                return (
                  <div className="mb-4">
                    <Popover open={showReadReceipts} onOpenChange={setShowReadReceipts} modal>
                      <PopoverTrigger asChild>
                        <button
                          className="inline-flex items-center gap-2 px-2 py-1 -ml-2 rounded-md hover:bg-muted/60 transition-colors cursor-pointer"
                          title={t`Confirmed ${confirmedCount}/${totalCount}`}
                        >
                          {/* 아바타 줄 — 최대 5명, 읽은 사람에는 체크 오버레이 */}
                          <div className="flex -space-x-1.5">
                            {mentionedIds.slice(0, 5).map(uid => {
                              const hasRead = readSet.has(uid);
                              return (
                                <div key={uid} className="relative">
                                  <UserAvatar
                                    src={getMemberPhoto(uid)}
                                    name={getMemberName(uid)}
                                    className="size-6 ring-2 ring-background"
                                  />
                                  {hasRead && (
                                    <Check className="absolute -bottom-0.5 -right-0.5 size-3 text-emerald-500" strokeWidth={3} />
                                  )}
                                </div>
                              );
                            })}
                            {mentionedIds.length > 5 && (
                              <div className="size-6 rounded-full ring-2 ring-background bg-muted flex items-center justify-center text-meta-sm font-semibold text-muted-foreground">
                                +{mentionedIds.length - 5}
                              </div>
                            )}
                          </div>
                          <span className={cn(
                            'text-xs font-semibold tabular-nums',
                            confirmedCount === totalCount ? 'text-emerald-500' : 'text-muted-foreground',
                          )}>
                            <Trans>Confirmed {confirmedCount}/{totalCount}</Trans>
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-72 p-2">
                        <div className="text-meta-sm font-semibold text-muted-foreground px-2 py-1.5 border-b border-border mb-1">
                          <Trans>Confirmation status ({confirmedCount}/{totalCount})</Trans>
                        </div>
                        <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
                          {mentionedIds.map(uid => {
                            const hasRead = readSet.has(uid);
                            const readAt = readMap[uid];
                            return (
                              <div key={uid} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/40">
                                <div className="relative shrink-0">
                                  <UserAvatar
                                    src={getMemberPhoto(uid)}
                                    name={getMemberName(uid)}
                                    className={cn('size-6', !hasRead && 'opacity-50 grayscale')}
                                  />
                                  {hasRead && (
                                    <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-emerald-500 ring-1 ring-background flex items-center justify-center">
                                      <CheckCircle2 className="size-2.5 text-white" strokeWidth={3} />
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs font-medium text-foreground truncate flex-1">
                                  {getMemberName(uid)}
                                </span>
                                {hasRead ? (
                                  <span className="text-meta-sm text-emerald-500 tabular-nums shrink-0">
                                    {new Date(readAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                                    {' '}
                                    {new Date(readAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-meta-sm text-muted-foreground/60 shrink-0">
                                    <Circle className="size-3" />
                                    <Trans>Unconfirmed</Trans>
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                );
              })()}

              {/* 태그 — 보기 모드 chip 표시 (편집 모드의 TagInput 은 본문 + 첨부 아래로 분리 배치) */}
              {!isEditing && post.tags && post.tags.length > 0 && (
                  <div className="flex gap-1.5 mt-4 flex-wrap">
                    {post.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary/8 text-primary/80 font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
              )}

              {/* Issue 속성 패널 */}
              {isIssueBoard && !isEditing && issueData && (
                <div className="mt-4 rounded-lg border border-border bg-muted/20 overflow-hidden">
                  {/* 헤더 — 모바일에서 토글, md 이상은 항상 표시 */}
                  <button
                    type="button"
                    onClick={() => setIssuePropsExpanded(v => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 md:pointer-events-none md:cursor-default cursor-pointer"
                  >
                    <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      <Trans>Issue properties</Trans>
                    </span>
                    <ChevronDown className={cn(
                      'size-3.5 text-muted-foreground transition-transform duration-200 md:hidden',
                      issuePropsExpanded && 'rotate-180',
                    )} />
                  </button>
                  {/* 콘텐츠 — 모바일 토글 / md 이상 항상 표시 */}
                  <div className={cn(
                    'space-y-2.5 px-3 pb-3 transition-all duration-200 ease-in-out',
                    issuePropsExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden',
                    'md:max-h-none md:opacity-100 md:overflow-visible',
                  )}>

                  {/* 담당자 */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <User className="size-3" /> <Trans>Assignee</Trans>
                    </span>
                    <Select
                      value={issueData.assignee?.uid ?? '__unassigned__'}
                      onValueChange={async (uid) => {
                        if (uid === '__unassigned__') {
                          const updated = await updateIssueAssignee(post.id, post.metadata, null);
                          setPost(updated);
                          toast({ title: t`Assignee cleared` });
                        } else {
                          const member = channelMembers.find(m => m.id === uid);
                          if (member) {
                            const updated = await updateIssueAssignee(post.id, post.metadata, {
                              uid: member.id,
                              name: member.name,
                              photoUrl: member.photoUrl ?? null,
                            });
                            setPost(updated);
                            toast({ title: t`Assigned to ${member.name}` });
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 w-auto min-w-[100px] max-w-[160px] gap-1 text-xs px-2 py-0 shadow-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__"><Trans>Unassigned</Trans></SelectItem>
                        {channelMembers.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* 마감일 */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Calendar className="size-3" /> <Trans>Due Date</Trans>
                    </span>
                    <div className="flex items-center gap-1.5">
                      {issueData.due_date && (() => {
                        const days = getDaysUntilDue(issueData.due_date);
                        const isOverdue = days < 0;
                        const isUrgent = days >= 0 && days <= 2;
                        return (
                          <span className={cn(
                            'text-meta-sm font-semibold px-1.5 py-0.5 rounded tabular-nums',
                            isOverdue ? 'text-destructive bg-destructive/10'
                              : isUrgent ? 'text-badge-warn bg-badge-warn-bg'
                                : 'text-muted-foreground bg-muted',
                          )}>
                            {isOverdue ? `D+${Math.abs(days)}` : days === 0 ? 'D-Day' : `D-${days}`}
                          </span>
                        );
                      })()}
                      <input
                        type="date"
                        value={issueData.due_date ?? ''}
                        onChange={async (e) => {
                          const updated = await updateIssueDueDate(post.id, post.metadata, e.target.value || null);
                          setPost(updated);
                          toast({ title: e.target.value ? t`Due date set` : t`Due date cleared` });
                        }}
                        className="h-6 rounded border border-border bg-background px-1.5 text-meta-sm text-foreground outline-none focus:ring-1 focus:ring-ring w-[120px]"
                      />
                    </div>
                  </div>
                </div>
                </div>
              )}

              {/* 본문 — 수정은 모달(PostCreateModal editingPost) 로 통합 */}
              <div className={cn(
                'mt-5 text-foreground leading-[1.75]',
                isNarrow ? 'text-body' : 'text-body-lg',
              )}>
                {post.content ? (
                  <BoardRichEditor
                    initialContent={post.content}
                    editable={false}
                    minHeight={0}
                    narrow={isNarrow}
                    wikiPages={isWikiBoard ? wikiPagesForMention : undefined}
                    onWikiPageClick={isWikiBoard ? (pageId) => {
                      fetchPost(pageId).then(p => {
                        if (p) setSelectedPost(p);
                      });
                    } : undefined}
                  />
                ) : (
                  <div className="whitespace-pre-wrap">
                    {post.plain_text || t`(No content)`}
                  </div>
                )}
              </div>

              {/* 첨부파일 — 보기 모드는 채팅과 동일한 표시(이미지 라이트박스/비디오 플레이어/문서 뷰어), 편집 모드는 PostCreateModal 로 통합 */}
              {!isEditing && post.attachments && post.attachments.length > 0 && (() => {
                const files = post.attachments.map(
                  att => new FileAttachment(att.filename, att.url, att.sizeByte, att.group),
                );
                return (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {files.map((file, idx) => (
                      <MessageFileItem
                        key={`${file.url}-${idx}`}
                        file={file}
                        files={files}
                        showTitle={false}
                      />
                    ))}
                  </div>
                );
              })()}
              {/* 편집 모드 (첨부/태그 추가·제거 등) 는 PostCreateModal editingPost 로 통합됨 */}

              {/* 리액션 바 — 채팅 스타일: 버튼 + 리액션된 이모지만 표시.
                  위키 본문은 카드 wrapper 가 없어 본문/리액션 구분이 약해서 border-top + 여백으로 가시성 강화. */}
              {!isEditing && (
                <>
                  <div className={cn(
                    'flex items-center gap-2 flex-wrap',
                    isWikiBoard && 'mt-6 pt-4 border-t border-border/40',
                  )}>
                    {/* 리액션 추가 버튼 (Popover) */}
                    <Popover open={showReactionPopover} onOpenChange={setShowReactionPopover} modal>
                      <PopoverTrigger asChild>
                        <Button
                          variant="icon-reaction"
                          data-state={showReactionPopover ? 'on' : undefined}
                        >
                          <IconSmilePlus />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="p-3 rounded-xl w-auto"
                        side="top"
                        align="start"
                        sideOffset={4}
                      >
                        <div className="flex items-center gap-1 px-1">
                          {(['smile', 'thumbs-up', 'heart', 'check', 'shake-hand'] as const).map(type => {
                            const isSaved = myReaction === type;
                            return (
                              <Button
                                key={type}
                                variant="ghost"
                                size="icon"
                                disabled={isLiking}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReaction(type);
                                  setShowReactionPopover(false);
                                }}
                                className={cn(
                                  'h-8 w-8 p-1 rounded-control hover:bg-muted transition-colors',
                                  isSaved && 'border-2 border-primary bg-primary/15 hover:bg-primary/25',
                                )}
                              >
                                <img
                                  src={`/assets/icon/ic-${type}-24.svg`}
                                  alt={type}
                                  className="w-full h-full"
                                />
                              </Button>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* 리액션된 이모지만 뱃지로 표시 — 클릭하면 유저 목록 */}
                    {(['smile', 'thumbs-up', 'heart', 'check', 'shake-hand'] as const).map(type => {
                      const uids = post.reactions[type] ?? [];
                      if (uids.length === 0) return null;
                      const isActive = myReaction === type;
                      return (
                        <Popover key={type} modal>
                          <PopoverTrigger asChild>
                            <button
                              className={cn(
                                'flex items-center gap-1 h-7 px-2.5 rounded-full text-sm transition-colors cursor-pointer',
                                isActive
                                  ? 'bg-primary/15 text-primary'
                                  : 'bg-muted hover:bg-muted/80',
                              )}
                            >
                              <img
                                src={`/assets/icon/ic-${type}-24.svg`}
                                alt={type}
                                className="size-4"
                              />
                              <span className={cn(
                                'tabular-nums text-xs font-medium',
                                isActive ? 'text-primary' : 'text-muted-foreground',
                              )}>
                                {uids.length}
                              </span>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-48 p-2"
                            side="top"
                            align="start"
                            sideOffset={4}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="space-y-1">
                              {uids.map(uid => {
                                const user = My.getUserFromRooms(uid);
                                const name = user?.name ?? uid.slice(0, 8);
                                const photo = user?.photoUrl ?? null;
                                const isMe = uid === My.user?.id;
                                return (
                                  <div key={uid} className="flex items-center gap-2 px-1 py-1 rounded-md">
                                    <UserAvatar src={photo} name={name} className="size-5 shrink-0" />
                                    <span className="text-xs font-medium text-foreground truncate flex-1">{name}</span>
                                    {isMe && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleReaction(type);
                                        }}
                                        className="text-meta-sm text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                                      >
                                        <Trans>Cancel</Trans>
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                      );
                    })}
                  </div>
                </>
              )}
              </div>
            </article>

            {/* 댓글 섹션 — 본문과 같은 너비로 정렬. 위키는 border-top hairline, 그 외는 카드 wrapper */}
            {!isEditing && (
              <div className={cn(
                'max-w-[1100px] mx-auto pb-8',
                isWikiBoard ? 'px-4' : 'px-4 md:px-6'
              )}>
                <div className={cn(
                  isWikiBoard
                    ? 'border-t border-border pt-6'
                    : 'bg-card border border-border rounded-xl shadow-sm px-6 md:px-8 py-5 md:py-6'
                )}>
                <CommentSection
                  postId={post.id}
                  comments={comments}
                  onCommentAdded={handleCommentAdded}
                  isQna={isQnaBoard}
                  post={post}
                  onPostUpdated={(updatedPost) => setPost(updatedPost)}
                  canComment={canComment}
                  mentionMembers={mentionMembers}
                  channelId={selectedBoard?.channel_id ?? undefined}
                  canMentionAll={canMentionAll}
                />
                </div>
              </div>
            )}
          </div>

          {/* ── 우측 TOC 사이드 패널 ──
              위키 보기 모드에서만 노출. 수정은 모달이 별도로 띄워지므로 여기엔 없음. */}
          {tocItems.length > 0 && (
            <>
              <aside className={cn(
                'w-[200px] shrink-0 border-l border-border bg-muted/10 overflow-y-auto',
                isNarrow ? 'hidden' : 'hidden lg:block',
              )}>
                <div className="p-3 sticky top-0">
                  <div className="flex items-center gap-1.5 mb-2">
                    <ListTree className="size-3.5 text-muted-foreground" />
                    <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      <Trans>Table of contents</Trans>
                    </span>
                    <button
                      type="button"
                      onClick={() => setTocHelpOpen(true)}
                      className="ml-auto inline-flex items-center justify-center size-5 rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors cursor-pointer"
                      title={t`Table of contents help`}
                      aria-label={t`Table of contents help`}
                    >
                      <HelpCircle className="size-3.5" />
                    </button>
                  </div>
                  <nav className="space-y-0.5">
                    {tocItems.map(item => (
                      <button
                        key={item.id}
                        className="w-full text-left text-meta text-muted-foreground hover:text-foreground truncate transition-colors cursor-pointer py-0.5"
                        style={{ paddingLeft: (item.level - 1) * 12 }}
                        onClick={() => {
                          const el = document.getElementById(item.id);
                          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        {item.text}
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* 모바일/태블릿 드로어 (xl 미만에서 노출) */}
              {tocOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-[2px] lg:hidden"
                    onClick={() => setTocOpen(false)}
                    aria-hidden
                  />
                  <aside className="fixed right-0 top-0 bottom-0 z-50 w-[260px] max-w-[80vw] border-l border-border bg-background shadow-xl overflow-y-auto animate-in slide-in-from-right duration-200 lg:hidden">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                      <div className="flex items-center gap-1.5">
                        <ListTree className="size-3.5 text-muted-foreground" />
                        <span className="text-meta font-semibold text-foreground"><Trans>Table of contents</Trans></span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setTocHelpOpen(true)}
                          className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted transition-colors cursor-pointer"
                          title={t`Table of contents help`}
                          aria-label={t`Table of contents help`}
                        >
                          <HelpCircle className="size-4" />
                        </button>
                        <button
                          onClick={() => setTocOpen(false)}
                          className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                          aria-label={t`Close`}
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    </div>
                    <nav className="p-2 space-y-0.5">
                      {tocItems.map(item => (
                        <button
                          key={item.id}
                          className="w-full text-left text-label text-foreground/80 hover:text-foreground hover:bg-muted/60 rounded px-2 py-1.5 truncate transition-colors cursor-pointer"
                          style={{ paddingLeft: (item.level - 1) * 12 + 8 }}
                          onClick={() => {
                            const el = document.getElementById(item.id);
                            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            setTocOpen(false);
                          }}
                        >
                          {item.text}
                        </button>
                      ))}
                    </nav>
                  </aside>
                </>
              )}
            </>
          )}
        </div>
        {/* /패널 */}
      </div>

      {/* 수정 이력 다이얼로그 */}
      {post && (
        <RevisionHistoryDialog
          postId={post.id}
          open={showRevisionHistory}
          onOpenChange={setShowRevisionHistory}
        />
      )}

      {/* 목차 도움말 모달 */}
      <TocHelpModal open={tocHelpOpen} onClose={() => setTocHelpOpen(false)} />

      {/* 수정 모달 — PostCreateModal 을 editingPost 로 재사용 */}
      {post && (
        <PostCreateModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          editingPost={post}
          onSaved={handleEditSaved}
        />
      )}
    </div>
  );
};

export default PostDetailOverlay;
